import {Pipe, PipeTransform, Injectable } from '@angular/core';

export type FilterMode = 'and' | 'or';
export type SortDirection = 'asc' | 'desc';

export interface ISortField {
    field: string;
    direction: SortDirection;
}

export interface IFilterParams {
    filter: string | null | undefined;
    fields: string[] | null;
    mode?: FilterMode;
    sortFields?: ISortField[] | null;
}

// Zoeksyntax (zoals het Google-zoekveld, plus "="):
//   woord          deel van een veldwaarde
//   "twee woorden" frase als geheel (spaties horen erbij), ook als deel van een veldwaarde
//   =woord         exact: de hele veldwaarde (getrimd, hoofdletterongevoelig), bv. =- voor een leeg-teken
//   ="twee woorden" exact, met spaties
// Een losse "=" is gewoon een zoekterm.
interface IFilterToken {
    text: string;
    exact: boolean;
}

const _tokenRegex = /(=?)"([^"]*)"|(=?)(\S+)/g;

function parseFilterTokens(input: string): IFilterToken[] {
    const _tokens: IFilterToken[] = [];
    let _match: RegExpExecArray | null;
    while ((_match = _tokenRegex.exec(input)) !== null) {
        const _exact = !!(_match[1] || _match[3]);
        const _text = _match[2] ?? _match[4];
        if (_text) _tokens.push({ text: (_exact ? _text.trim() : _text).toLowerCase(), exact: _exact });
        else if (_match[3]) _tokens.push({ text: '=', exact: false });
    }
    return _tokens;
}

// Genormaliseerd vergelijken: Date → epoch, boolean → 0/1, strings via localeCompare (geen vaste
// locale — zelfde reden als elders in de app, zie feedback_global_locale_date_formatting).
function compareValues(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    const _a = a instanceof Date ? a.getTime() : typeof a === 'boolean' ? Number(a) : a;
    const _b = b instanceof Date ? b.getTime() : typeof b === 'boolean' ? Number(b) : b;

    if (typeof _a === 'string' && typeof _b === 'string') {
        return _a.localeCompare(_b, undefined, { sensitivity: 'base', numeric: true });
    }
    return _a < _b ? -1 : _a > _b ? 1 : 0;
}

// Meerdere sorteervelden = prioriteitsketen (1e veld primair, 2e tie-breaker, enz.) — opgebouwd via
// herhaald klikken in FilterComponent's instellingenpaneel. .slice() vóór .sort(): Array.sort is
// sinds ES2019 stable, dus items die op alle gekozen velden gelijk zijn behouden hun oorspronkelijke
// (server-)volgorde — maar de input-array van de pipe mag niet gemuteerd worden.
function sortItems<T>(items: T[], sortFields: ISortField[]): T[] {
    if (!sortFields?.length) return items;

    return items.slice().sort((a, b) => {
        for (const { field, direction } of sortFields) {
            const _cmp = compareValues((a as any)[field], (b as any)[field]);
            if (_cmp !== 0) return direction === 'asc' ? _cmp : -_cmp;
        }
        return 0;
    });
}

@Pipe({
    name: 'filterSearch',
    standalone: true,
})

@Injectable()
export class FilterPipe implements PipeTransform {

    transform(items: any[], params: IFilterParams): any[] {
        return filterSearch(items, params);
    }
}

// Zelfde filter + sortering als de pipe, maar los aanroepbaar — de lijst-tools rekenen hun zichtbare
// lijst hiermee uit als computed (listFiltered$$), zodat ook het aantal (bv. de scrollpositie-
// overlay) beschikbaar is i.p.v. alleen in de template.
export function filterSearch<T>(items: T[] | null | undefined, params: IFilterParams): T[] {
    if (!items || items.length === 0) return [];

    const _tokens = parseFilterTokens(params.filter ?? '');
    const _fields = params.fields ?? [];
    const _mode: FilterMode = params.mode ?? 'and';

    const _matchToken = (item: any, token: IFilterToken) =>
        _fields.some(f => {
            const _value = item[f]?.toString().toLowerCase();
            if (_value == null) return false;
            return token.exact ? _value.trim() === token.text : _value.includes(token.text);
        });

    const _filtered = _tokens.length === 0 ? items : items.filter(item =>
        _mode === 'or'
            ? _tokens.some(t => _matchToken(item, t))
            : _tokens.every(t => _matchToken(item, t))
    );

    return sortItems(_filtered, params.sortFields ?? []);
}
